// src/components/billing/SubscriptionActivationManager.js
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import BillingActivationBanner from "./BillingActivationBanner";

const BILLING_OVERVIEW_URL =
  process.env.REACT_APP_BILLING_OVERVIEW_URL ||
  process.env.REACT_APP_GET_ORG_CONTEXT_URL ||
  "https://getorgcontext-774073159618.us-central1.run.app";

const callAuthedJson = async (url, payload = {}) => {
  if (!url) throw new Error("Missing API URL env var.");
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated.");
  const token = await user.getIdToken();

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || "Request failed.");
  return json;
};

const parseDate = (v) => {
  if (!v) return null;

  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === "object") {
    if (typeof v?.seconds === "number") {
      const d = new Date(v.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof v?._seconds === "number") {
      const d = new Date(v._seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
};

const isActiveStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return s === "active" || s === "trialing";
};

export default function SubscriptionActivationManager() {
  const location = useLocation();
  const navigate = useNavigate();

  const [authed, setAuthed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const [needsActivation, setNeedsActivation] = React.useState(false);
  const [dismissUntil, setDismissUntil] = React.useState(null);

  // ✅ Hide banner only until page refresh (in-memory flag)
  const [bannerHiddenUntilRefresh, setBannerHiddenUntilRefresh] = React.useState(false);

  // Banner dismiss UX loading state (kept for ButtonComponent spinner)
  const [dismissSaving, setDismissSaving] = React.useState(false);

  // Don’t show this on login / account-action / post-login / portal routes.
  const isAuthScreen =
    location.pathname === "/login" ||
    location.pathname === "/account-action" ||
    location.pathname === "/post-login";

  const isPortalRoute = location.pathname.startsWith("/portal");

  React.useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);

      // ✅ If auth changes (login/logout), allow banner again (still "until refresh" scope)
      // This is safe and keeps behavior intuitive.
      setBannerHiddenUntilRefresh(false);
    });
    return () => unsub();
  }, []);

  // ✅ If the user navigates around, we should NOT permanently hide;
  // keep it hidden only until refresh. Do not reset on route change.

  const refresh = React.useCallback(async () => {
    if (!authed) {
      setNeedsActivation(false);
      setDismissUntil(null);
      setLoading(false);
      return;
    }

    if (!BILLING_OVERVIEW_URL) {
      setNeedsActivation(false);
      setDismissUntil(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await callAuthedJson(BILLING_OVERVIEW_URL, {});
      const org = data?.org || {};
      const summary = data?.billingSummary || {};

      const active =
        isActiveStatus(summary?.subscriptionStatus) ||
        isActiveStatus(summary?.subscriptionStatusByMode?.live) ||
        isActiveStatus(summary?.subscriptionStatusByMode?.test);

      const dUntil = parseDate(org?.billingActivation?.dismissUntil);

      setNeedsActivation(!active);
      setDismissUntil(dUntil);
    } catch (e) {
      // Fail open (don’t block app)
      setNeedsActivation(false);
      setDismissUntil(null);
    } finally {
      setLoading(false);
    }
  }, [authed]);

  React.useEffect(() => {
    if (isAuthScreen || isPortalRoute) return;
    refresh();
  }, [refresh, isAuthScreen, isPortalRoute, location.pathname]);

  if (isAuthScreen || isPortalRoute) return null;
  if (!authed) return null;
  if (loading) return null;
  if (!needsActivation) return null;

  const now = new Date();
  const withinDismissWindow = dismissUntil && now < dismissUntil;

  // ✅ Manager is banner-only.
  // Gate is handled by PostLoginRouter (and only shown when NOT dismissed).
  if (!withinDismissWindow) return null;

  // ✅ Hide only until refresh
  if (bannerHiddenUntilRefresh) return null;

  const onActivate = () => navigate("/settings/billing");

  // ✅ Banner dismiss = hide until refresh only (no CF call)
  const onDismiss = async () => {
    setDismissSaving(true);
    try {
      setBannerHiddenUntilRefresh(true);
    } finally {
      setDismissSaving(false);
    }
  };

  return (
    <BillingActivationBanner
      dismissUntil={dismissUntil}
      onActivate={onActivate}
      onDismiss={onDismiss}
      dismissing={dismissSaving}
    />
  );
}